import venueApiService from '../services/venueApi';
import { getDefaultFullVenueHourlyRate } from './venuePricing';

export const KIJU_COFFEE_VENUE_ID = '660e8400-e29b-41d4-a716-446655440023';

/** Landing cards that show a fixed label instead of hourly rate (not on public pricing whitelist). */
export const FEATURED_VENUE_CUSTOM_PRICING_LABEL = 'Custom pricing';

export const FEATURED_VENUE_CUSTOM_PRICING_IDS = new Set([KIJU_COFFEE_VENUE_ID]);

/** Static card labels when API is slow/unavailable. */
export const FEATURED_VENUE_PRICE_FALLBACKS = {
  '660e8400-e29b-41d4-a716-446655440007': '£120/hr',
};

const formatRate = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)));
};

/** Public homepage card price from base hourly rate + active dynamic rules. */
export const formatFeaturedVenuePrice = (venue, dynamicPricing = []) => {
  const base = getDefaultFullVenueHourlyRate(venue);
  const dynamicRates = (dynamicPricing || [])
    .filter((rule) => rule?.is_active !== false)
    .map((rule) => Number(rule.price_per_hour))
    .filter((n) => Number.isFinite(n) && n > 0);

  const rates = [...new Set([base, ...dynamicRates].filter((n) => n != null && n > 0))].sort(
    (a, b) => a - b
  );

  if (!rates.length) return null;

  const min = formatRate(rates[0]);
  const max = formatRate(rates[rates.length - 1]);
  if (!min) return null;
  if (min === max) return `£${min}/hr`;
  return `£${min}/hr - £${max}/hr`;
};

export const formatFeaturedVenueCapacity = (venue) => {
  const capacity = Number(
    venue?.max_capacity ??
      venue?.maxCapacity ??
      venue?.capacity ??
      venue?.standing_capacity ??
      venue?.standingCapacity
  );
  return Number.isFinite(capacity) && capacity > 0 ? String(capacity) : null;
};

/** Same first photo as the venue details gallery (primary, then newest). */
export function getVenueDetailsFirstImage(venue) {
  const images = Array.isArray(venue?.images) ? [...venue.images] : [];
  if (images.length) {
    images.sort((a, b) => {
      if (a.is_primary && !b.is_primary) return -1;
      if (!a.is_primary && b.is_primary) return 1;
      return new Date(b.uploaded_at) - new Date(a.uploaded_at);
    });
    const src = images[0]?.image_url || images[0]?.url || images[0]?.src;
    if (src) return src;
  }
  return venue?.primaryImageUrl || venue?.primary_image_url || null;
}

export async function fetchFeaturedVenueStats(venueId) {
  const id = String(venueId || '');

  if (FEATURED_VENUE_CUSTOM_PRICING_IDS.has(id)) {
    try {
      const venue = await venueApiService.getVenueById(id);
      return {
        price: FEATURED_VENUE_CUSTOM_PRICING_LABEL,
        capacity: formatFeaturedVenueCapacity(venue),
        image: getVenueDetailsFirstImage(venue),
      };
    } catch {
      return {
        price: FEATURED_VENUE_CUSTOM_PRICING_LABEL,
        capacity: null,
        image: null,
      };
    }
  }

  const fallbackPrice = FEATURED_VENUE_PRICE_FALLBACKS[id] ?? null;

  try {
    const [venue, dynamicPricing] = await Promise.all([
      venueApiService.getVenueById(id),
      venueApiService.getDynamicPricing(id),
    ]);

    return {
      price: formatFeaturedVenuePrice(venue, dynamicPricing) ?? fallbackPrice,
      capacity: formatFeaturedVenueCapacity(venue),
      image: getVenueDetailsFirstImage(venue),
    };
  } catch {
    return {
      price: fallbackPrice,
      capacity: null,
      image: null,
    };
  }
}
